import { useState, useEffect } from 'react';
import { getOCELMetadata } from '../../../../services/apiClient';

/**
 * Custom hook to fetch event attribute names from metadata
 * Supports both global attributes and activity-specific attributes
 * @param {string} activeFile - The active OCEL file name
 * @param {string} activity - Optional activity name to filter attributes
 * @returns {Object} - { eventAttributes: string[], loading: boolean }
 */
export const useEventAttributes = (activeFile, activity = null) => {
  const [eventAttributes, setEventAttributes] = useState(['ocel:timestamp']);
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);

  // Fetch metadata when activeFile changes
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!activeFile) {
        setMetadata(null);
        return;
      }

      setLoading(true);
      try {
        console.log('🔄 [useEventAttributes] Fetching metadata for:', activeFile);
        const data = await getOCELMetadata(activeFile);
        console.log('✅ [useEventAttributes] Metadata fetched:', {
          hasData: !!data,
          eventTypesCount: data?.event_types?.length || 0
        });
        setMetadata(data);
      } catch (error) {
        console.error('❌ [useEventAttributes] Error fetching metadata:', error);
        setMetadata(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [activeFile]);

  // Extract attributes when metadata or activity changes
  useEffect(() => {
    if (!metadata) {
      console.log('⏳ [useEventAttributes] Waiting for metadata to load...');
      setEventAttributes(['ocel:timestamp']);
      return;
    }

    console.log('🔄 [useEventAttributes] Extracting event attributes:', {
      activeFile,
      activity,
      hasEventTypes: !!metadata?.event_types,
      eventTypesCount: metadata?.event_types?.length || 0
    });

    let attrs = [];

    // If activity is specified, get activity-specific attributes
    if (activity && metadata?.event_types) {
      console.log('🔍 [useEventAttributes] Searching for activity:', activity);

      const eventType = metadata.event_types.find(et => et.name === activity);

      if (eventType) {
        console.log('✅ [useEventAttributes] Found event type:', eventType);
      }

      if (eventType && eventType.attributes && eventType.attributes.length > 0) {
        // Extract attribute names from activity-specific attributes
        const activityAttrs = eventType.attributes.map(attr => attr.name);
        // Only show activity-specific attributes (don't include ocel:timestamp)
        attrs = activityAttrs;
        console.log(`✅ [useEventAttributes] Activity-specific attributes for "${activity}":`, activityAttrs);
        console.log(`🎯 [useEventAttributes] FINAL attrs (without ocel:timestamp):`, attrs);
      } else {
        // No activity-specific attributes, use ocel:timestamp as default
        attrs = ['ocel:timestamp'];
        console.log(`⚠️ [useEventAttributes] No activity-specific attributes found for "${activity}", using ocel:timestamp`);
      }
    } else {
      // No activity selected, use ocel:timestamp as default
      attrs = ['ocel:timestamp'];
      console.log('⚠️ [useEventAttributes] No activity selected, using ocel:timestamp');
    }

    // Fallback to global event attributes if no activity or no activity-specific attrs found
    if (attrs.length === 0 && metadata?.event_attribute_names) {
      const globalAttrs = metadata.event_attribute_names;
      attrs = [...new Set(['ocel:timestamp', ...globalAttrs])]; // Combine and deduplicate
      console.log('✅ [useEventAttributes] Global event attributes loaded:', globalAttrs);
    }

    // Ensure we always have at least ocel:timestamp if nothing else is available
    if (attrs.length === 0) {
      attrs = ['ocel:timestamp'];
      console.log('⚠️ [useEventAttributes] No attributes found, fallback to ocel:timestamp');
    }

    console.log('🎯 [useEventAttributes] Setting eventAttributes to:', attrs);
    setEventAttributes(attrs);
  }, [metadata, activity, activeFile]);

  return { eventAttributes, loading };
};
